import { Injectable }       from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                 from '../api/api.service';
import { GoogleSsoClientCredentials } from './models/google-sso.client-credentials';

export type GoogleSsoFlowType = 'login' | 'link';

@Injectable({
  providedIn: 'root'
})
export class GoogleSsoService {
  public errorMessage : string = '';
  public loginUrl              = 'https://accounts.google.com/o/oauth2/v2/auth';

  private _credentialsPromise : Promise<GoogleSsoClientCredentials>;
  private _flowStorageKey     = 'google_sso_flow';
  private _stateStorageKey    = 'google_sso_state';

  constructor (
    private apiService      : ApiService,
    private translateService: TranslateService
  ) {}

  clearStoredState () : void {
    sessionStorage.removeItem(this._stateStorageKey);
    sessionStorage.removeItem(this._flowStorageKey);
  }

  generateState ( flow : GoogleSsoFlowType = 'login' ) : string {
    const bytes = new Uint8Array(16);

    window.crypto.getRandomValues(bytes);

    const state = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');

    sessionStorage.setItem(this._stateStorageKey, state);
    sessionStorage.setItem(this._flowStorageKey, flow);

    return state;
  }

  getClientCredentials () : Promise<GoogleSsoClientCredentials> {
    if ( !this._credentialsPromise ) {
      this._credentialsPromise = this.apiService.get('/google_sso/get_google_client_credentials')
      .catch(error => {
        this._credentialsPromise = null;

        return Promise.reject(error);
      });
    }

    return this._credentialsPromise;
  }

  getLinkUrl () : Promise<string> {
    return this.getClientCredentials()
    .then(credentials => {
      const params = new URLSearchParams({
        client_id    : credentials.client_id,
        redirect_uri : this.getRedirectUri(),
        response_type: 'code',
        scope        : 'openid profile email',
        state        : this.generateState('link')
      });

      return `${ this.loginUrl }?${ params.toString() }`;
    });
  }

  getRedirectUri () : string {
    return `${ window.location.origin }/login.html`;
  }

  getStoredFlow () : GoogleSsoFlowType {
    return sessionStorage.getItem(this._flowStorageKey) as GoogleSsoFlowType;
  }

  handleLinkFlow ( params : any ) : Promise<any> {
    if ( params.error ) {
      return Promise.reject(`${ window.location.origin }/workshop.html#/user_settings?google_link_error=${ encodeURIComponent(params.error) }`);
    }

    return this.link(params.code, this.getRedirectUri())
    .then(() => Promise.resolve(`${ window.location.origin }/workshop.html#/user_settings?google_link_success=true`))
    .catch(( error : any ) => {
      const message = error?.error?.text || error?.message || this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.GOOGLE_SSO.ACCOUNT_LINKING_FAILED');

      return Promise.reject(`${ window.location.origin }/workshop.html#/user_settings?google_link_error=${ encodeURIComponent(message) }`);
    })
  }

  isValidState ( state : string ) : boolean {
    const storedState = sessionStorage.getItem(this._stateStorageKey);

    return !!state && !!storedState && state === storedState;
  }

  link ( code : string, redirectUri : string ) : Promise<any> {
    return this.apiService.post('/google_sso/link', {
      code        : code,
      redirect_uri: redirectUri
    });
  }

  unlink () : Promise<any> {
    return this.apiService.delete('/google_sso/unlink');
  }
}
